import { Box, MenuItem, Stack } from "@mui/material";
import { Ref, forwardRef, useEffect, useImperativeHandle} from "react";
import { useTranslation } from "react-i18next";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { getObjectTypeName } from "../../common/utilities/GetObjectTypeName";
import { AppNotificationTypeEnum } from "../../contexts/app-notification-context/AppNotificationContextTypes";
import { useAppNotificationContext } from "../../contexts/app-notification-context/UseAppNotificationContext";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { useGroupedSetpointData } from "../../queries/alarm-setpoint-admin/UseGroupedSetpointData";
import { useObjectsWithoutSetpointsData } from "../../queries/alarm-setpoint-admin/UseObjectsSetpointsData";
import { useSetpointData } from "../../queries/alarm-setpoint-admin/UseSetpointData";
import { useSetpointsWithoutObjectData } from "../../queries/alarm-setpoint-admin/UseSetpointWithoutObjectsData";
import { useGroup } from "../../queries/groups/UseGroup";
import { RestError } from "../../services/rest-service/RestService";
import SetpointAdministrationDataService from "../../services/setpoint-administration-service/SetpointAdministrationService";
import AlarmSetpointEditorDialog, { useAlarmSetpointEditorDialogState, bindAlarmSetpointEditorDialog } from "../alarm-setpoint-editor/AlarmSetpointEditorDialog";
import AlarmSetpointTestDisableDialog, { useAlarmSetpointTestOrDisableDialogState, bindAlarmSetpointTestOrDisableDialog } from "../alarm-setpoint-test-disable-dialog/AlarmSetpointTestDisableDialog";
import { ContentContainer } from "../content-container/ContentContainer";
import { ContentSection } from "../content-section/ContentSection";
import { ViewOptionStateEnum } from "../contextual-app-bar/ViewOptionStateEnum";
import GroupedVirtualizedList from "../grouped-virtualized-list/GroupedVirtualizedList";
import LoadingZone from "../loading-zone/LoadingZone";
import NoData from "../no-data/NoData";
import OptionsSideSheet from "../options-side-sheet/OptionsSideSheet";
import SideSheetFilterInput from "../side-sheet/side-sheet-filter-input/SideSheetFilterInput";
import SideSheetSelect from "../side-sheet/side-sheet-select/SideSheetSelect";
import VirtualizedList from "../virtualized-list/VirtualizedList";
import { AdminSetpointListItem } from "./AdminSetpointListItem";
import useAdminSetpointListState from "./AdminSetpointListState";
import AdminSetpointListUtils, { SetPointGrouping, Setpoint, SetPointListActionEnum, ObjectNoSetpoint, setpointGroupingOptions  } from "./AdminSetpointListUtils";
import { ObjectListItem } from "./ObjectListItem";
import { useSimilarSetpointData } from "../../queries/alarm-setpoint-admin/UseSimilarSetpointData";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import SideSheet from "../side-sheet/side-sheet/SideSheet";
import { isMobile, isTablet } from "react-device-detect";
import { BaseActionEnum, BaseActions } from "../action-menu/ActionMenuItem";
import RenameConfigurationDialog, { bindRenameConfigurationDialog, useRenameConfigurationDialogState } from "../rename-configuration-dialog/RenameConfigurationDialog";
import { useSaveSetpoint } from "../../queries/alarm-setpoint-admin/UseSaveSetpoint";
import { contains } from "../../common/utilities/Contains";


export type AlarmSetpointListType = {
    executeAction: (action: SetPointListActionEnum | BaseActions) => void;
};

export type AdminSetpointListProps = {
    groupID: number;
    setViewOptionState?: (state: ViewOptionStateEnum) => void;
    selectable?: boolean;
    linkedSetpoints?: number[],
    onSelected?: (setpoints: number[]) => void,
    openSideSheet?: boolean;
    onCloseSideSheet?: () => void;

};

function AdminSetpointList({
    groupID,
    setViewOptionState,
    selectable,
    linkedSetpoints,
    onSelected,
    openSideSheet,
    onCloseSideSheet
}: AdminSetpointListProps, ref: Ref<unknown> | undefined) {

    const isPhone = isMobile && !isTablet;

    const [userState] = useUserContext();
    const [,setAppNotificationState] = useAppNotificationContext();
    const { t } = useTranslation();
    const { confirm, acknowledge } = useConfirmation();
    const routeContext = useRouteContext();

    const setpointEditorDialog = useAlarmSetpointEditorDialogState();
    const setpointTestDisableDialog = useAlarmSetpointTestOrDisableDialogState();
    const renameConfigurationDialog = useRenameConfigurationDialogState();
        
    const {
        state,
        setFilterText,
        setGrouping,
        setSelectedSetpoints,
        resetState,
    } = useAdminSetpointListState();

    const isGrouped = state.groupingOption !== SetPointGrouping.AllAlarms &&
    state.groupingOption !== SetPointGrouping.SetPointsWithoutObjects && 
    state.groupingOption !== SetPointGrouping.ObjectsWithoutAlarms;

    const {data: group } = useQueryNotifications(useGroup)({
        customerID: userState.customer?.id as number,
        groupID
    });

    const {data, status, refetch: refetchSetpoints } = useQueryNotifications(useSetpointData)({
        groupID,
        searchCriteria: {
        },
        enabled: !isGrouped && !selectable
    });

    const {data: similarSetpoints, status: similarStatus } = useQueryNotifications(useSimilarSetpointData)({
        groupID,
        organizationID: group?.organizationID ?? -1,
        tagID: routeContext?.tagID ?? -1,
        enabled: selectable && group !== undefined && routeContext.tagID !== undefined ? true : false
    });

    const {data: groupedData, status: groupedStatus, refetch: refetchGroupedData } = useQueryNotifications(useGroupedSetpointData)({
        groupID,
        objectType: state.groupObjectType ?? eLynxObjectTypeEnum.Device,
        searchCriteria: {
        },
        enabled: isGrouped
    });

    const {data: setpointsNoObjects, refetch: refetchSetpointsNoObjects, status: setpointNoObjectsStaus } = useQueryNotifications(useSetpointsWithoutObjectData)({
        groupID,
        searchCriteria: {
        },
        enabled: state.groupingOption === SetPointGrouping.SetPointsWithoutObjects
    });

    const {data: objectsNoSetpoints, status: objectsNoSetpointsStatus, refetch: refectObjectsNoSetpoints } = useQueryNotifications(useObjectsWithoutSetpointsData)({
        groupID,
        searchCriteria: {
        },
        enabled: state.groupingOption === SetPointGrouping.ObjectsWithoutAlarms
    });

    const { mutate: saveSetpoint/*, status*/ } = useQueryNotifications(useSaveSetpoint)({
        groupID: groupID,
        organizationID: group?.organizationID as number,
        onSuccess: async (data) => {
            acknowledge(t("setpointAdmin.successMessageUpdated", { 
                                name: data.setPointName 
                        }));

            refetchSetpoints();
        }
    });
    

    const isLoading = status === "loading" || 
                        groupedStatus === 'loading' || 
                        setpointNoObjectsStaus === 'loading' || 
                        objectsNoSetpointsStatus === 'loading' ||
                        similarStatus === 'loading';

    const setpoints = state.groupingOption === SetPointGrouping.ObjectsWithoutAlarms ? 
                        undefined :
                        isGrouped ?
                            AdminSetpointListUtils.getGroupedData(groupedData, state.filterText) :
                        state.groupingOption === SetPointGrouping.SetPointsWithoutObjects ?
                            setpointsNoObjects?.setPoints.filter(o => contains(o.name, state.filterText))
                            .map(o => {
                                const item = {...o,
                                    groupKey: ''
                                } as Setpoint
                        
                                return item;
                            }) :

                        !selectable ?    
                        data?.setPoints.filter(o => contains(o.name, state.filterText))
                                    .map(o => {
                                    
                                    const item = {...o,
                                        groupKey: '',
                                    } as Setpoint

                                    return item;
                        }) : 
                    
                        similarSetpoints?.setPoints.filter(o => contains(o.name, state.filterText))
                                    .map(o => {
                                        const item = {...o,
                                            groupKey: '',
                                            selected: state.selectedSetpoints.find(v => v === o.id) ? true : false
                                        } as Setpoint

                                        return item;
                        }).filter(o => (linkedSetpoints ?? []).find(v => o.id === v) === undefined);

    const objects = state.groupingOption === SetPointGrouping.ObjectsWithoutAlarms ?
                    AdminSetpointListUtils.getNoSetpointData(objectsNoSetpoints, state.filterText) :
                    undefined;

    const nodata = state.groupingOption === SetPointGrouping.ObjectsWithoutAlarms ?
                        objectsNoSetpointsStatus === 'success' && 
                        (objects?.length ?? 0) === 0 :
                    setpoints?.length === 0 && 
                    ((!isGrouped && status === "success") || (isGrouped && groupedStatus === 'success') || similarStatus === 'success') 

    useImperativeHandle<unknown, AlarmSetpointListType>(
        ref,
        () => ({
             executeAction: executeAction
        })
    );
    
    async function onEditSetpoint(setpoint: Setpoint, copy?: boolean) {

        const result = await setpointEditorDialog.open({
            groupID,
            setpointID: setpoint.id,
            organizationID: group?.organizationID as number,
            copy
        });

        if (result.reason === 'save') {

            if (!isGrouped) {
                await refetchSetpoints();
            } else {
                await refetchGroupedData();            
            }

            refetchSetpointsNoObjects();
        }
    }

    async function onRenameSetpoint(setpoint: Setpoint) {

        const result = await renameConfigurationDialog.open({
            configurationName: setpoint.name,
            configurationType: t("objectTypeNames.setpoint"),
        });

        if (result.reason === "ok" && result.values) {
            try {
                const entity = await SetpointAdministrationDataService.getSetpoint(groupID, group!.organizationID!, setpoint.id);
                entity.setPointName = result.values.configurationName;
                saveSetpoint(entity);
            } catch (error) {
                const restError = error as RestError;
                
                setAppNotificationState({
                    notificationType: AppNotificationTypeEnum.Error,
                    show: true,
                    error: restError
                });
            }
        }
    }

    async function onCopySetpoint(setpoint: Setpoint) {

        const result = await confirm({
            dialogTitle: t("setpointAdmin.copy.title"),
            confirmationText: t("setpointAdmin.copy.text"),
            yesButtonText: t("buttons.yesCopy"),
            noButtonText: t("buttons.noCancel"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true,
            showCloseButton: true
        });

        if (result === 'yes') {
            onEditSetpoint(setpoint, true);
        }
    }

    async function onTestDisableSetpoint(item: Setpoint) {
        await setpointTestDisableDialog.open({
            groupID,
            setpoint: item,
        });


        if (!isGrouped) {
            await refetchSetpoints();
        } else {
            await refetchGroupedData();
        }

    }

    async function onDeleteSetpoint(item: Setpoint) {

        try {

            const usasgePromise = SetpointAdministrationDataService.getSetpointUsage(groupID, item.id);
            const setpointPromise = SetpointAdministrationDataService.getSetpoint(groupID, group?.organizationID as number, item.id);

            const [usage, setpoint] = await Promise.all([usasgePromise, setpointPromise]);

            if (setpoint.tags.length === usage.length) {

                const result = await confirm({
                    dialogTitle: t("deleteConfirmation.title", {itemName: 'Alarm Setpoint'}),
                    confirmationText: item.linkedTagCount > 1 ?
                                t("setpointAdmin.delete.multiTagConfirmation", {
                                    count: setpoint.tags.length
                                }) : 
                            item.linkedTagCount === 1 ? 
                                t("setpointAdmin.delete.oneTagConfirmation") :
                                t("setpointAdmin.delete.confirmation"),
                    yesButtonText: t("deleteConfirmation.yesButtonText"),
                    noButtonText: t("deleteConfirmation.noButtonText"),
                    showConfirmationText: true,
                    showYesButton: true,
                    showNoButton: true,
                    showCloseButton: true
                });

                if (result !=="yes") {
                    return;
                }

                await SetpointAdministrationDataService.deleteSetpoint(groupID, item.id);

                acknowledge(t('setpointAdmin.delete.success'));

            } else {

                const result = await confirm({
                    dialogTitle: t("deleteConfirmation.title", {itemName: 'Alarm Setpoint'}),
                    confirmationText: t("setpointAdmin.delete.tagMultiGroupConfirmation", {
                                    totalTagCount: setpoint.tags.length,
                                    linkedTagCount: usage.length
                                }),
                    yesButtonText: t("setpointAdmin.delete.unlinkTags"),
                    noButtonText: t("setpointAdmin.delete.doNotUnlinkTags"),
                    showConfirmationText: true,
                    showYesButton: true,
                    showNoButton: true,
                    showCloseButton: true
                });

                if (result !=="yes") {
                    return;
                }

                usage.forEach(o => {

                    const ndx = setpoint.tags.findIndex(v => v.tagID === o.tagID);
                    if (ndx !== -1) {
                        setpoint.tags.splice(ndx, 1);
                    }
                })

                await SetpointAdministrationDataService.saveSetpoint(groupID, setpoint);

                acknowledge(t("setpointAdmin.successMessageUpdated", { 
                        name: setpoint.setPointName 
                    }
                ));
            }
        } catch (error) {
            const restError = error as RestError;
            
            setAppNotificationState({
                notificationType: AppNotificationTypeEnum.Error,
                show: true,
                error: restError
            });

        } finally {

            await Promise.all([refetchSetpoints(), 
                refetchGroupedData(),
                refetchSetpointsNoObjects(),
                refectObjectsNoSetpoints()
            ]);
        }
    }

    function onSetpointSelected(payload: Setpoint) {

        let items = [...state.selectedSetpoints];
        if (payload.selected) {

            items = items.filter(o => o !== payload.id);
        } else {

            items.push(payload.id);
        }

        setSelectedSetpoints(items);

        onSelected?.(items);
    }

    function onSetpointListAction(action: SetPointListActionEnum, payload: Setpoint) {

        switch (action) {
            case SetPointListActionEnum.Edit:
                onEditSetpoint(payload);
                break;

            case SetPointListActionEnum.Delete:
                onDeleteSetpoint(payload);
                break;

            case SetPointListActionEnum.Copy:
                onCopySetpoint(payload);
                break;


            case SetPointListActionEnum.DisableTest:
                onTestDisableSetpoint(payload);
                break;

            case SetPointListActionEnum.Select:
                onSetpointSelected(payload);
                break;

            case SetPointListActionEnum.Rename:
                onRenameSetpoint(payload);
                break;

        }
    }

    function renderItem(_index: number, item: Setpoint) {
        return (
            <AdminSetpointListItem
                key={`${item.id}`}
                groupID={groupID}
                item={item}
                onAction={onSetpointListAction}
                selectable={selectable}
            />
        );
    }

    function renderGroupItem(_index: number, _groupKey: string, item: Setpoint) {
        return (
            <AdminSetpointListItem
                groupID={groupID}
                item={item}
                key={`${item.id}`}
                onAction={onSetpointListAction}
            />
        );
    };

    function renderNoSetpointGroupItem(_index: number, _groupKey: string, item: ObjectNoSetpoint) {
        return (
            <ObjectListItem
                item={item}
                key={`${item.id}`}
            />
        );
    };


    function renderGroup(groupKey: string) {

        const groupText = groupKey;
        return (
            <Box sx={{
                    backgroundColor: "custom.background.1",
                    height: theme => theme.spacing(6),
                    width: 1,
                    paddingLeft: 1,
                    display: "flex",
                    alignItems: "center"
                }}>
                {groupText}
            </Box>
        );
    }

     function executeAction(action: SetPointListActionEnum | BaseActionEnum) {

        let items: number[] = [];
        switch (action) {
            // case BaseActionEnum.Refresh:
            //     refetch();
            //     break;

            case SetPointListActionEnum.SelectAll:
                items = setpoints?.map(o => o.id) ?? [];
                break;

            case SetPointListActionEnum.RemoveAll:
                break;
        }

        setSelectedSetpoints(items);
        onSelected?.(items);
     }


     useEffect(() => {
        setViewOptionState?.(state.viewOptionState);
    }, [
        state.viewOptionState,
        setViewOptionState
    ]);

    return (
        <LoadingZone isLoading={isLoading}>

            <ContentContainer>
                <ContentSection>


                    {nodata ? 
                    <NoData
                        title={t("setpointAdmin.nodata.title")}
                        text={<Stack
                            direction="row"
                            spacing={0.5}
                            sx={{
                                alignItems: "flex-end"
                            }}>
                            <Box>
                                {isGrouped ? t("setpointAdmin.nodata.groupedText", {objectType: getObjectTypeName(state.groupObjectType)}) : t("setpointAdmin.nodata.text")}
                            </Box>
                        </Stack>
                        }
                    /> :
                    null
                }

                {state.groupingOption === SetPointGrouping.ObjectsWithoutAlarms &&
                <GroupedVirtualizedList
            
                    items={objects ?? []}
                    renderItem={renderNoSetpointGroupItem}
                    renderGroup={renderGroup}
                />
                }


                {isGrouped &&
                <GroupedVirtualizedList
                        items={setpoints ?? []}
                        renderItem={renderGroupItem}
                        renderGroup={renderGroup}
                    />
                }

                {!isGrouped && state.groupingOption !== SetPointGrouping.ObjectsWithoutAlarms &&
                    <VirtualizedList
                        items={setpoints ?? []} 
                        renderItem={renderItem}
                    />
                }

                </ContentSection>

                {!selectable ? <OptionsSideSheet onReset={resetState}>
                    <SideSheetSelect
                        label={t('setpointAdmin.display')}
                        value={state.groupingOption.toString()}
                        onChange={(value: string) => {
                            setGrouping(+value);
                            }}>
                        {setpointGroupingOptions.map((o, index) => {
                            return (
                                <MenuItem key={index} value={o.value}>
                                    {o.text}
                                </MenuItem>
                            );
                        })}
                    </SideSheetSelect> 


                    <SideSheetFilterInput
                        label={t("setpointAdmin.filterBy")}
                        value={state.filterText}
                        onChange={(value: string) => {
                            setFilterText(value);
                        }}
                    />


                </OptionsSideSheet> : 
                <SideSheet                
                    title={t("options")}
                    open={openSideSheet ?? false}
                    hideCloseButton={!isPhone}
                    onClose={() => onCloseSideSheet && onCloseSideSheet()}
                    onReset={resetState}
                >

                    <SideSheetFilterInput
                        label={t("setpointAdmin.filterBy")}
                        value={state.filterText}
                        onChange={(value: string) => {
                            setFilterText(value);
                        }}
                    />

                </SideSheet>

                }
            </ContentContainer>

            <AlarmSetpointEditorDialog {...bindAlarmSetpointEditorDialog(setpointEditorDialog)} />
            <AlarmSetpointTestDisableDialog {...bindAlarmSetpointTestOrDisableDialog(setpointTestDisableDialog)} />
            <RenameConfigurationDialog {...bindRenameConfigurationDialog(renameConfigurationDialog)} />
        </LoadingZone>
    );
}

export default forwardRef(AdminSetpointList);